'use client';

/**
 * Status Banner Component
 *
 * Real-time health indicators for system status
 * Inspired by Vercel/Datadog status bars
 *
 * @example
 * // Mothership (full system status)
 * <StatusBanner
 *   items={[
 *     { label: 'API', status: 'operational', icon: Server },
 *     { label: 'Database', status: 'operational', icon: Database },
 *   ]}
 * />
 *
 * // Consumer app (simple status)
 * <StatusBanner
 *   items={[{ label: 'System Status', status: 'operational', icon: Activity }]}
 *   compact
 * />
 */

import React from 'react';
import { Activity, LucideIcon } from 'lucide-react';

export type StatusLevel = 'operational' | 'degraded' | 'down' | 'retrying' | 'maintenance';

export interface StatusItem {
  label: string;
  status: StatusLevel;
  icon?: LucideIcon;
}

export interface StatusBannerProps {
  items: StatusItem[];
  title?: string;
  onViewDetails?: () => void;
  compact?: boolean;
}

const statusConfig: Record<StatusLevel, { label: string; dot: string; text: string }> = {
  operational: { label: 'Operational', dot: 'bg-green-400', text: 'text-green-400' },
  degraded: { label: 'Degraded', dot: 'bg-yellow-400', text: 'text-yellow-400' },
  down: { label: 'Down', dot: 'bg-red-400', text: 'text-red-400' },
  retrying: { label: 'Retrying', dot: 'bg-amber-400', text: 'text-amber-400' },
  maintenance: { label: 'Maintenance', dot: 'bg-blue-400', text: 'text-blue-400' },
};

function StatusIndicator({ item }: { item: StatusItem }) {
  const config = statusConfig[item.status];
  const Icon = item.icon || Activity;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 border border-white/5 hover:border-white/10 transition-colors">
      <Icon className="h-4 w-4 text-white/60" />
      <span className="text-xs font-medium text-white/80">{item.label}</span>
      <div className="flex items-center gap-1.5 ml-auto">
        <div className={`h-2 w-2 rounded-full ${config.dot} ${item.status === 'operational' ? '' : 'animate-pulse'}`} />
        <span className={`text-xs font-medium ${config.text}`}>
          {config.label}
        </span>
      </div>
    </div>
  );
}

export function StatusBanner({ items, title, onViewDetails, compact = false }: StatusBannerProps) {
  const allOperational = items.every(item => item.status === 'operational');
  const defaultTitle = allOperational ? 'All Systems Operational' : 'System Status';

  if (compact) {
    const worstStatus = items.reduce((worst, item) => {
      const priority: StatusLevel[] = ['down', 'degraded', 'retrying', 'maintenance', 'operational'];
      return priority.indexOf(item.status) < priority.indexOf(worst) ? item.status : worst;
    }, 'operational' as StatusLevel);

    const config = statusConfig[worstStatus];

    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900/60 border border-white/10">
        <div className={`h-2 w-2 rounded-full ${config.dot} ${worstStatus === 'operational' ? '' : 'animate-pulse'}`} />
        <span className={`text-xs font-medium ${config.text}`}>
          {title || defaultTitle}
        </span>
      </div>
    );
  }

  return (
    <div
      className="bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
      onClick={onViewDetails}
      role={onViewDetails ? 'button' : undefined}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`h-2.5 w-2.5 rounded-full ${allOperational ? 'bg-green-400' : 'bg-amber-400'} ${allOperational ? '' : 'animate-pulse'}`} />
          <span className="text-sm font-semibold text-white">
            {title || defaultTitle}
          </span>
        </div>
        {onViewDetails && (
          <button type="button" className="text-xs text-white/50 hover:text-white/80 transition-colors">
            View Details →
          </button>
        )}
      </div>

      <div className={`grid gap-2 ${
        items.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' :
        items.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' :
        items.length <= 4 ? 'grid-cols-2 sm:grid-cols-4' :
        'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
      }`}>
        {items.map((item, index) => (
          <StatusIndicator key={item.label + index} item={item} />
        ))}
      </div>
    </div>
  );
}
